import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/constants';

/**
 * Referred By Autocomplete
 * -----------------------------------------------------------------------
 * Typeahead for the "Referred By" (Referral Partner) field on the Add/Edit
 * Job form. Fetches matching partners from /api/referral-partners as the
 * user types. If no exact match exists, offers an inline "+ Add" row that
 * creates a new partner on the fly (no leaving the form).
 *
 * Props
 *   value                    { id?, name }   currently selected partner
 *   onChange({ id, name })   fires on select OR clear (id may be null when cleared)
 *   placeholder              placeholder text
 *   testId                   data-testid for the input
 */
export const ReferredByAutocomplete = ({
  value,
  onChange,
  placeholder = 'Start typing partner name…',
  testId = 'referred-by-input',
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value?.name || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Keep in sync if the parent replaces the value externally (e.g. on load or reset)
  useEffect(() => {
    if ((value?.name || '') !== inputValue && document.activeElement !== inputRef.current) {
      setInputValue(value?.name || '');
    }
  }, [value?.name, value?.id]);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referral-partners`, { params: { search: q } });
      setSuggestions(res.data || []);
    } catch (err) {
      console.error('Partner search failed:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setInputValue(v);
    setShowList(true);
    setActiveIndex(-1);

    // If user clears the field, notify parent so the id is dropped too
    if (!v.trim() && value?.id) {
      onChange({ id: null, name: '' });
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (v.trim().length >= 1) fetchSuggestions(v.trim());
      else fetchSuggestions('');  // show top 200 on empty focus
    }, 200);
  };

  const selectPartner = (p) => {
    setInputValue(p.name);
    setShowList(false);
    onChange({ id: p.id, name: p.name });
  };

  const addNewPartner = async () => {
    const name = inputValue.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API}/referral-partners`, { name });
      const p = res.data;
      selectPartner(p);
      toast.success(`Added "${p.name}" to referral partners`);
    } catch (err) {
      console.error('Add partner failed:', err);
      toast.error('Could not add partner');
    }
  };

  // Exact match (case-insensitive) present in current suggestions?
  const normalized = inputValue.trim().toLowerCase();
  const exactMatch = suggestions.find((s) => (s.normalized_name || s.name.toLowerCase()) === normalized);
  const showAddNew = !!normalized && !exactMatch;

  // Rows: [ ...suggestions, optional AddNew ] — keyboard navigation covers both
  const rowCount = suggestions.length + (showAddNew ? 1 : 0);

  const handleKeyDown = (e) => {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(rowCount - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        selectPartner(suggestions[activeIndex]);
      } else if (showAddNew && (activeIndex === suggestions.length || activeIndex === -1)) {
        e.preventDefault();
        addNewPartner();
      }
    } else if (e.key === 'Escape') {
      setShowList(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInput}
        onFocus={() => {
          setShowList(true);
          // Prefetch a broad list on focus so first key press feels instant
          if (suggestions.length === 0) fetchSuggestions(inputValue.trim());
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
      />
      {showList && (loading || suggestions.length > 0 || showAddNew) && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            maxHeight: '260px',
            overflowY: 'auto',
          }}
        >
          {loading && suggestions.length === 0 && (
            <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>Searching…</div>
          )}
          {suggestions.map((s, idx) => (
            <div
              key={s.id}
              role="option"
              aria-selected={idx === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); selectPartner(s); }}
              onMouseEnter={() => setActiveIndex(idx)}
              data-testid={`referred-by-option-${s.id}`}
              style={{
                padding: '0.55rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
                backgroundColor: idx === activeIndex ? '#f1f5f9' : 'transparent',
              }}
            >
              {s.name}
            </div>
          ))}
          {showAddNew && (
            <div
              role="option"
              aria-selected={activeIndex === suggestions.length}
              onMouseDown={(e) => { e.preventDefault(); addNewPartner(); }}
              onMouseEnter={() => setActiveIndex(suggestions.length)}
              data-testid="referred-by-add-new"
              style={{
                padding: '0.6rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
                borderTop: suggestions.length > 0 ? '1px solid #f1f5f9' : 'none',
                backgroundColor: activeIndex === suggestions.length ? '#ecfdf5' : '#f9fafb',
                color: '#047857',
                fontWeight: 600,
              }}
            >
              + Add &ldquo;{inputValue.trim()}&rdquo; as new partner
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferredByAutocomplete;
